/**
 * Popover-based select — replaces native <select> for a consistent premium feel.
 * Fixed-position menu (works inside modals), Esc/outside-click to close,
 * flips up when there is no room below.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function Select({
  value,
  onChange,
  options,
  disabled,
  ariaLabel,
  className,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const openMenu = () => {
    const el = triggerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const gap = 6;
      const pad = 12;
      const width = Math.max(rect.width, 240);
      const left = Math.max(pad, Math.min(rect.left, window.innerWidth - width - pad));
      const spaceBelow = window.innerHeight - rect.bottom - pad;
      const spaceAbove = rect.top - pad;
      const maxHeight = Math.min(320, Math.max(160, Math.max(spaceBelow, spaceAbove) - gap));
      if (spaceBelow >= Math.min(220, maxHeight) || spaceBelow >= spaceAbove) {
        setPos({ top: rect.bottom + gap, left, width, maxHeight });
      } else {
        setPos({ bottom: window.innerHeight - rect.top + gap, left, width, maxHeight });
      }
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={cn(
          'w-full inline-flex items-center justify-between gap-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-3.5 py-2.5 text-left text-[13px] text-[#e8e6e3] transition-colors',
          'hover:border-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a] disabled:opacity-40',
          className,
        )}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          {selected?.icon && (
            <span className="shrink-0 text-[#5a5a5a]">{selected.icon}</span>
          )}
          <span className="truncate">{selected?.label ?? value}</span>
        </span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 shrink-0 text-[#5a5a5a] transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && pos && (
        <div
          ref={menuRef}
          role="listbox"
          aria-label={ariaLabel}
          className="fixed z-[100] overflow-y-auto scrollbar-thin bg-[#161616] border border-[#2a2a2a] rounded-xl shadow-2xl py-1 od-enter"
          style={{
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 transition-colors flex items-start gap-2.5',
                  active ? 'bg-[#1f1f1f]' : 'hover:bg-[#222]',
                )}
              >
                {o.icon && (
                  <span
                    className={cn(
                      'mt-0.5 shrink-0',
                      active ? 'text-[#e8e6e3]' : 'text-[#5a5a5a]',
                    )}
                  >
                    {o.icon}
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span
                    className={cn(
                      'block text-[13px] leading-snug',
                      active ? 'text-[#e8e6e3] font-medium' : 'text-[#c8c6c3]',
                    )}
                  >
                    {o.label}
                  </span>
                  {o.description && (
                    <span className="block text-[11px] text-[#5a5a5a] mt-0.5 leading-snug">
                      {o.description}
                    </span>
                  )}
                </span>
                {active && (
                  <Check className="w-3.5 h-3.5 text-[#e8e6e3] shrink-0 mt-0.5" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
